import { getHeaders, useToast } from '@get-mosh/msh-shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import ApiRoutes from 'src/constants/ApiRoutes';
import { env } from 'src/env.mjs';
import { ApiError } from 'src/types/Api';
import { useRouter, useSearchParams } from 'next/navigation';
import Routes from 'src/constants/Routes';

export type CreateAccountResponse = {
    uuid: string;
    accessToken: string;
    refreshToken: string;
};

type CreateAccountRequest = {
    email: string;
    password: string;
};

export type VerifyUserResponse = {
    message: string;
};

type VerifyUserEmailRequest = {
    email: string;
};

type VerifyUserPhoneRequest = {
    phoneNumber: string;
};

const useCreateAccountSuccess = () => {
    const queryClient = useQueryClient();
    const router = useRouter();
    const searchParams = useSearchParams();

    return (response: CreateAccountResponse) => {
        Cookies.set(
            env.NEXT_PUBLIC_COOKIE_PREFIX + 'mosh_ut_v2',
            response.accessToken,
            {
                expires: 4 / 24,
                domain: env.NEXT_PUBLIC_COOKIE_DOMAIN,
            },
        );
        Cookies.set(
            env.NEXT_PUBLIC_COOKIE_PREFIX + 'mosh_uuid',
            response.uuid,
            {
                expires: 4 / 24,
                domain: env.NEXT_PUBLIC_COOKIE_DOMAIN,
            },
        );

        queryClient.invalidateQueries({
            queryKey: ['User'],
        });

        const redirect = searchParams.get('redirect_url');
        router.push(redirect || Routes.WELCOME);
    };
};

async function postCreateAccount(
    request: CreateAccountRequest,
): Promise<CreateAccountResponse> {
    const res = await fetch(ApiRoutes.CREATE_ACCOUNT, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(request),
    });
    const data = await res.json();

    if (!res.ok) {
        throw data as ApiError;
    }

    return data as CreateAccountResponse;
}

export const useCreateAccount = () => {
    const createAccountSuccess = useCreateAccountSuccess();
    const { showToast } = useToast();

    return useMutation<CreateAccountResponse, ApiError, CreateAccountRequest>({
        mutationKey: ['CreateAccount'],
        mutationFn: postCreateAccount,
        onError: (error, request) => {
            const response = error as ApiError;
            if (response.type === 'email_sent') {
                window.location.assign(Routes.VERIFY_EMAIL(request.email));
            } else {
                showToast({
                    title: 'An error occurred',
                    message: 'Please try again later',
                    variant: 'error',
                });
            }
        },
        onSuccess: createAccountSuccess,
    });
};

async function postVerifyEmailUser(
    request: VerifyUserEmailRequest,
): Promise<VerifyUserResponse> {
    const res = await fetch(ApiRoutes.VERIFY_EMAIL, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(request),
    });
    const data = await res.json();

    if (!res.ok) {
        throw data as ApiError;
    }
    return data as VerifyUserResponse;
}

export const useVerifyEmailUser = () => {
    const { showToast } = useToast();

    return useMutation<VerifyUserResponse, ApiError, VerifyUserEmailRequest>({
        mutationKey: ['VerifyUser'],
        mutationFn: (request: VerifyUserEmailRequest) =>
            postVerifyEmailUser(request),
        onError: (error) => {
            const response = error as ApiError;
            if (response) {
                showToast({
                    title: 'An error occurred',
                    message: 'Please try again later',
                    variant: 'error',
                });
            }
        },
        onSuccess: () => {
            showToast({
                title: 'Email Sent',
                message: 'Please check your inbox',
                variant: 'success',
            });
        },
    });
};

async function postVerifyPhoneUser(
    request: VerifyUserPhoneRequest,
): Promise<VerifyUserResponse> {
    const res = await fetch(ApiRoutes.VERIFY_PHONE, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(request),
    });
    const data = await res.json();

    if (!res.ok) {
        throw data as ApiError;
    }
    return data as VerifyUserResponse;
}

export const useVerifyPhoneUser = () => {
    const { showToast } = useToast();

    return useMutation<VerifyUserResponse, ApiError, VerifyUserPhoneRequest>({
        mutationKey: ['VerifyUserPhone'],
        mutationFn: (request: VerifyUserPhoneRequest) =>
            postVerifyPhoneUser(request),
        onError: (error) => {
            const response = error as ApiError;
            if (response) {
                showToast({
                    title: 'An error occurred',
                    message: 'Please try again later',
                    variant: 'error',
                });
            }
        },
        onSuccess: () => {
            showToast({
                title: 'SMS Sent',
                message: 'Please check your messages',
                variant: 'success',
            });
        },
    });
};
